import { useRef, useEffect, useState, useCallback } from "react";

const THRESHOLD = 60;
const AXIS_LOCK_PX = 8;

export const usePullToRefresh = (onRefresh) => {
  const [pulling, setPulling] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const startY = useRef(0);
  const startX = useRef(0);
  const active = useRef(false);
  const axisLocked = useRef(false); // becomes true once we've confirmed a vertical pull
  const distanceRef = useRef(0); // live distance read in touchend (avoids stale closure)
  const refreshingRef = useRef(false);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    refreshingRef.current = true;
    setPullDistance(0);
    distanceRef.current = 0;
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
      refreshingRef.current = false;
    }
  }, [onRefresh]);

  useEffect(() => {
    // Touch-only: don't hijack scroll on desktop or touch-capable laptops with a mouse.
    const isCoarse =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(pointer: coarse)").matches;
    if (!isCoarse) return undefined;

    const reset = () => {
      active.current = false;
      axisLocked.current = false;
      setPulling(false);
      setPullDistance(0);
      distanceRef.current = 0;
    };

    const onTouchStart = (e) => {
      if (window.scrollY > 5) return;
      startY.current = e.touches[0].clientY;
      startX.current = e.touches[0].clientX;
      active.current = true;
      axisLocked.current = false;
    };

    const onTouchMove = (e) => {
      if (!active.current || refreshingRef.current) return;
      const dy = e.touches[0].clientY - startY.current;
      const dx = e.touches[0].clientX - startX.current;

      // Lock the axis on first meaningful movement: bail out of horizontal gestures
      // (carousels, filter chips, edge back-swipe) so they don't trigger a refresh.
      if (!axisLocked.current) {
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > AXIS_LOCK_PX) {
          active.current = false;
          return;
        }
        if (Math.abs(dy) > AXIS_LOCK_PX) {
          axisLocked.current = true;
        } else {
          return; // movement too small to classify yet
        }
      }

      if (dy < 0) {
        reset();
        return;
      }
      const dampened = Math.min(dy * 0.4, 100);
      setPulling(true);
      setPullDistance(dampened);
      distanceRef.current = dampened;
    };

    const onTouchEnd = () => {
      if (!active.current) {
        axisLocked.current = false;
        return;
      }
      active.current = false;
      axisLocked.current = false;
      setPulling(false);
      if (distanceRef.current >= THRESHOLD) {
        handleRefresh();
      } else {
        setPullDistance(0);
        distanceRef.current = 0;
      }
    };

    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: true });
    document.addEventListener("touchend", onTouchEnd);

    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
    };
  }, [handleRefresh]);

  return { pulling, refreshing, pullDistance };
};
